import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { Box, ClickAwayListener, Fade, Paper, Popper, TextField } from '@mui/material';
import { Action } from '@reduxjs/toolkit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

type Props = {
    initialComment?: string;
    open: boolean;
    canWrite: boolean;
    saveCommentActionCreator(comment: string | null): Action;
    setOpen(open: boolean): void;
};

export const CommentPopperButton = ({
    initialComment,
    saveCommentActionCreator,
    open,
    setOpen,
    canWrite
}: Props) => {
    const [comment, setComment] = useState(null as string | null);
    const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
    const anchorRef = useRef<SVGSVGElement>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        setComment(initialComment ?? null);
    }, [initialComment]);

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleClose = (event: globalThis.MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        if (!anchorRef.current?.contains(target)) {
            if (canWrite && (comment ?? undefined) !== initialComment) {
                dispatch(saveCommentActionCreator(comment));
            }

            setOpen(false);
        }
    };

    const handleSetComment = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (canWrite) {
                setComment(event.target.value);
            }
        },
        [canWrite, setComment]
    );

    const handleOnKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                if (comment !== initialComment) {
                    dispatch(saveCommentActionCreator(comment));
                }
                setOpen(false);
            }

            if (e.key === 'Escape') {
                setOpen(false);
                setComment(initialComment ?? null);
            }

            e.stopPropagation();
        },
        [comment, initialComment, dispatch, saveCommentActionCreator, setOpen]
    );

    const handleOnClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
    };

    return (
        <Box component="div" sx={{ display: 'flex', padding: '2px' }}>
            <ChatBubbleIcon
                aria-haspopup="true"
                onClick={handleToggle}
                color={initialComment != null ? 'primary' : 'secondary'}
                opacity={0.5}
                fontSize="small"
                ref={anchorRef}
                sx={{
                    color: initialComment != null ? 'rgba(0, 39, 255, 1)' : 'rgba(63, 81, 181, 0.42)',
                    fontSize: initialComment != null ? '1rem' : undefined
                }}
            />

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom"
                sx={theme => ({
                    zIndex: 2,
                    '&[x-placement*="top"] $arrow': {
                        bottom: 0,
                        left: 0,
                        marginBottom: '-0.9em',
                        width: '3em',
                        height: '1em',
                        '&::before': {
                            borderWidth: '1em 1em 0 1em',
                            borderColor: `${theme.palette.background.paper} transparent transparent transparent`
                        }
                    },
                    '&[x-placement*="bottom"] $arrow': {
                        top: 0,
                        left: 0,
                        marginTop: '-0.9em',
                        width: '3em',
                        height: '1em',
                        '&::before': {
                            borderWidth: '0 1em 1em 1em',
                            borderColor: `transparent transparent ${theme.palette.background.paper} transparent`
                        }
                    }
                })}
                modifiers={[
                    {
                        name: 'arrow',
                        enabled: true,
                        options: {
                            element: arrowRef
                        }
                    }
                ]}
                transition
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={e => handleClose(e)}>
                        <Fade {...TransitionProps} timeout={350}>
                            <Box component="div">
                                <Box
                                    component="span"
                                    sx={{
                                        position: 'absolute',
                                        fontSize: 7,
                                        width: '3em',
                                        height: '3em',
                                        '&::before': {
                                            content: '""',
                                            margin: 'auto',
                                            display: 'block',
                                            width: 0,
                                            height: 0,
                                            borderStyle: 'solid'
                                        }
                                    }}
                                    ref={setArrowRef}
                                />
                                <Paper sx={{ width: '400px', padding: '8px' }}>
                                    <TextField
                                        label="Comment"
                                        onChange={handleSetComment}
                                        value={comment ?? ''}
                                        autoFocus
                                        variant="standard"
                                        onKeyDown={handleOnKeyDown}
                                        inputProps={{ maxLength: 100 }}
                                        fullWidth={true}
                                        onClick={handleOnClick}
                                        onDoubleClick={handleOnClick}
                                        disabled={!canWrite}
                                        onFocus={handleFocus}
                                    />
                                </Paper>
                            </Box>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </Box>
    );
};
